import React from 'react';

import { AiRulesPickerField } from '@lumapps/ai-pickers/components/AiRulesPickerField/AiRulesPickerField';
import { AiFeedbackAndReactions } from '@lumapps/ai/components/AiFeedbackAndReactions';
import { AskAiRule, SortKeys } from '@lumapps/ask-ai-rules/types';
import { useClassnames } from '@lumapps/classnames';
import { robotSparkle } from '@lumapps/lumx/custom-icons';
import { FlexBox, Icon } from '@lumapps/lumx/react';

import { trackAskCopy } from '../../api/analytics';
import { useAskAiFeedback } from '../../hooks/useAskAiFeedback';
import { AskSearchBestAnswer } from '../../types';

import './index.scss';

export interface AskAiAnswerFooterProps {
    className?: string;
    answer: AskSearchBestAnswer;
    /** Unique ID to identify each search */
    traceId: string;
    /** The query from which the answer has been generated, used for tracking */
    query?: string;
    /** Whether the component is used in the micro-apps builder context, only for preview */
    isPreview?: boolean;
    /** Should we display the agent name or not */
    shouldDisplayAgentName?: boolean;
    /** If we should animate the component when rendered */
    isAnimated?: boolean;
    /** Icon sizes */
    size?: 's' | 'm';
    /** Handle change agentId - receives the new rule and the message ID */
    handleAgentChange?: (rule: AskAiRule, messageId: string) => void;
    /** Whether the answer is being streamed */
    isStreaming?: boolean;
}

const CLASSNAME = 'ask-ai-answer-footer';

/**
 * Display the answer given by the Ask AI api with some extra design
 */
export const AskAiAnswerFooter: React.FC<AskAiAnswerFooterProps> = ({
    className,
    answer,
    traceId,
    query,
    isPreview,
    shouldDisplayAgentName = false,
    isAnimated = false,
    size = 's',
    handleAgentChange,
    isStreaming,
}) => {
    const { block, element } = useClassnames(CLASSNAME);
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    const { isFeedbackReasonsBlockOpen, closeFeedbackReason, feedbackType, onFeedbackClick, sendFeedback } =
        useAskAiFeedback({
            traceId,
            answer,
            parentElement: buttonRef,
        });

    /** Send a copy event once the answer has been copied (not tracked in preview) */
    const onCopyClick = () => {
        const copyQuery = query || answer.query;
        if (isPreview || !copyQuery || !traceId) {
            return;
        }
        trackAskCopy(traceId, copyQuery);
    };

    return (
        <AiFeedbackAndReactions
            className={block({ animated: isAnimated }, className)}
            feedbackType={feedbackType}
            isFeedbackReasonsOpen={isFeedbackReasonsBlockOpen}
            onFeedbackClick={onFeedbackClick}
            sendFeedback={sendFeedback}
            onCloseFeedbackReasons={closeFeedbackReason}
            contentToCopy={answer.answer}
            size={size}
            isPreview={isPreview}
            disableAfterFeedback
            scope="ask-ai-reactions"
            afterReaction={
                shouldDisplayAgentName && answer.matchedRule?.title ? (
                    <FlexBox
                        orientation="horizontal"
                        gap="regular"
                        hAlign="center"
                        className={element('picker-container')}
                    >
                        <AiRulesPickerField
                            value={answer.matchedRule as AskAiRule}
                            onChange={(rule) => rule && handleAgentChange?.(rule, traceId)}
                            className={element('agent-picker')}
                            before={<Icon icon={robotSparkle} />}
                            popoverClassName={element('rules-popover')}
                            isDisabled={isStreaming}
                            getRulesParams={{ sort: SortKeys.AZ_TITLE }}
                        />
                    </FlexBox>
                ) : undefined
            }
            onCopyClick={onCopyClick}
        />
    );
};
